<template>
    <div class="auth">
        <page-loader :loading="loading || !isSetupDone"/>

        <full-screen-dialog
            v-if="isSetupDone && !loading"
            :header-back-button-disabled="navigationDisabled"
            header-close-button-disabled
            @close="handleClose"
            @go-back="handleNavigation"
        >
        <div :style="backgroundStyle">
            <div class="container auth-container">
                <ThirdPartyBranding
                    :referring-platform-app-resource="referringPlatformAppResource"
                >
                    <div>
                        <email-step
                            v-if="activeStep === 'email'"
                            :errors="errors.email"
                            :loading="false"
                            :is-branded="!!referringPlatformAppResource.branded"
                            :value="credentials"
                            @submit="submitEmail"
                        />
                        <password-step
                            v-else-if="activeStep === 'signIn'"
                            :errors="errors.password"
                            :loading="false"
                            :value="credentials"
                            @input="clearErrors"
                            @submit="signIn"
                        />
                        <select-state
                            v-else-if="activeStep === 'selectState'"
                            :errors="errors.state"
                            :loading="false"
                            :value="state"
                            @welcome="handleNavigation"
                            @submit="submitState"
                        />
                        <choose-type-step
                            v-else-if="activeStep === 'chooseType'"
                            :loading="false"
                            :value="basicInfo"
                            @backToEmail="backToEmail"
                            @welcome="handleNavigation"
                            @submit="chooseAccountType"
                        />
                        <confirm-password-step
                            v-else-if="activeStep === 'confirmPassword'"
                            :loading="false"
                            :errors="errors"
                            :value="credentials"
                            :accountTypeId="basicInfo.accountType"
                            :referringPlatform="params.referringPlatform"
                            @backToEmail="backToEmail"
                            @submit="confirmPassword"
                        />
                        <NotInvitedSeller
                            v-else-if="activeStep === 'notInvitedSeller'"
                            :loading="false"
                            @welcome="() => backTo('chooseType')"
                        />
                    </div>
                </ThirdPartyBranding>
            </div>
        </div>


        </full-screen-dialog>
    </div>
</template>

<script>
import './Auth.scss';
import {useAuthStore} from '@store';
import {mapActions} from 'pinia';
import EmailStep from "./Steps/EmailStep/EmailStep";
import PasswordStep from './Steps/PasswordStep/PasswordStep';
import ChooseTypeStep from './Steps/ChooseTypeStep/ChooseTypeStep';
import SelectState from "./Steps/SelectState/SelectState";
import ConfirmPasswordStep from './Steps/ConfirmPassword/ConfirmPasswordStep';
import NotInvitedSeller from './Steps/NotInvitedSeller/NotInvitedSeller';
import {AGENT, TRANSACTION_COORDINATOR} from '@sellers-shield-home-protection/ss-core-js/src/AppResources/LuAccountTypeAppResource';
import {get, isEmpty} from "lodash";
import {runAsPublic} from "@helpers/api-helper";
import {isSeller} from "@helpers/user";
import {isValidEmail} from "@helpers/email";
import backgroundPath from "@assets/images/Rectangle1.png";
import {
    appPageRedirect,
    appUrlOrPathnameByUserType,
    pageRedirect,
    paramsFromQueryString,
    paramsToQueryString
} from "@helpers/router-helper";
import AuthOrRegisterMixin from '@mixins/AuthOrRegisterMixin';
import SegmentAnalyticsMixin from '@mixins/SegmentAnalyticsMixin';
import FrontEndApiService from "@services/frontend.api.service";

const STEPS = {
        email: 'email',
        confirmPassword: 'confirmPassword',
        signIn: 'signIn',
        selectState: 'selectState',
        chooseType: 'chooseType',
        notInvitedSeller: 'notInvitedSeller'
    },
    STEPS_GROUPS = {
        default: [
            STEPS.email,
            STEPS.selectState,
            STEPS.chooseType,
        ]
    };

export default {
    name: 'Auth',
    mixins: [
        AuthOrRegisterMixin,
        SegmentAnalyticsMixin
    ],

    components: {
        SelectState,
        EmailStep,
        PasswordStep,
        ChooseTypeStep,
        ConfirmPasswordStep,
        NotInvitedSeller
    },

    data: () => ({
        activeStep: STEPS.email,
        STEPS_GROUPS,
        backgroundStyle: {
          backgroundImage: `url(${backgroundPath})`,
          backgroundSize: '100% 100%',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          width: '100vw',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          padding: '20px',
          position: 'absolute',
          top: 0,
          left: 0
        },
    }),

    computed: {
        navigationDisabled() {
            return [STEPS.email].includes(this.activeStep);
        },
    },

    methods: {
        ...mapActions(useAuthStore, {
            register: 'register',
        }),
        async submitEmail() {
            this.clearErrors();
            let {email} = this.credentials;
            return await this.loadingWrapper(async () => {
                if (!email || !isValidEmail(email)) {
                    this.errors.email.push(email ? 'Please input a valid email address' : 'Email is required');
                    return;
                }
                this.userExists = await this.searchExistingUser(email);

                if(this.userExists && this.passwordNotSet){
                    //Move to the Transaction Coordinator Page if Password is not set
                    await this.checkConnectionInviation(null, email);

                    this.activeStep = STEPS.confirmPassword;

                    return;
                }

                if (this.userExists) {
                    this.activeStep = STEPS.signIn;
                    return;
                }

                let noRedirect = !(
                    await runAsPublic(async () => await this.checkAgentInvites()) ||
                    await runAsPublic(async () => await this.checkSellerInvites())
                );

                if (noRedirect) {
                    this.activeStepByNavFlow(STEPS.selectState);
                    return true;
                }
            });
        },
        async submitState(metaObj = {}, skipStep = false) {
            this.clearErrors();
            let {state} = this.state;

            return await this.loadingWrapper(async () => {
                if (!state) {
                    this.errors.state.push('Please select a state');
                    return;
                }

                if (!(await this.checkStateApplicationVersion())) {
                    return;
                }

                skipStep || this.activeStepByNavFlow(STEPS.chooseType);
                return !skipStep;
            });
        },
        async checkStateApplicationVersion() {
            let params = this.params;

            return await this.checkAppVersionByEmailState(
                this.credentials?.email,
                this.state?.state,
                params.redirectUrl,
                {
                    'accountType': params.type || 'agent',
                    'accessToken': params.accessToken || null,
                    'authCallbackUrl': params.authCallbackUrl || null,
                    'referringPlatform': params.referringPlatform || null,
                    'developerListingId': params.developerListingId || 'new'
                }
            );
        },
        async signIn() {
            //TODO: PE-775 here is where re-check the agents license validity if they have a texas license
            this.clearErrors();
            let credentials = this.credentials,
                params = this.params;

            return await this.loadingWrapper(async () => {
                try {
                    let user = await this.login(
                            credentials?.email,
                            credentials?.password
                        ),
                        userUuid = user?.uuid;

                    //show loading screen no matter what
                    this.isSetupDone = false;


                    if (Boolean(params.redirectUrl)) {
                        await pageRedirect(params.redirectUrl);
                    } else if (userUuid) {

                        await this.segmentCapture(user, this.segmentActions.LOGIN, null);
                        let redirectTo = appUrlOrPathnameByUserType(user?.accountTypeId);
                        if (params.token && isSeller(user)) {
                            await pageRedirect(redirectTo, `/orders/token-link-account/${params.token}`);
                        } else {
                            await pageRedirect(redirectTo, redirectTo === window.env.APP_URL ? '' : '/dashboard');
                        }
                    } else {
                        await pageRedirect(window.env.APP_URL);
                    }
                } catch (error) {
                    console.error(error)
                    this.errors.password = ['Invalid password!']
                }
            });
        },
        async chooseAccountType() {
            this.clearErrors();
            return await this.loadingWrapper(async () => {
                if (!(await this.checkStateApplicationVersion())) {
                    return;
                }
                let accountType = `${this.basicInfo?.accountType}`.toLowerCase();

                switch (accountType) {
                    case 'seller':
                        this.activeStep = STEPS.notInvitedSeller
                        break;
                    case 'agent':
                    case 'broker':
                        // Should redirect from guest
                        this.params.email = this.credentials?.email;
                        this.params.state = this.state?.state;
                        await appPageRedirect(accountType, `/register?${paramsToQueryString(this.params)}`);
                        break;
                    case 'transactioncoordinator':
                        this.params.email = this.credentials?.email;
                        this.params.state = this.state?.state;
                        await appPageRedirect(accountType, `/register?${paramsToQueryString(this.params)}`);
                        break;
                    default:
                        await appPageRedirect(null, `/not-found`)
                        break;
                }
            });
        },
        async confirmPassword(){
            this.clearErrors();
            const {password, confirmPassword} = this.credentials;

            if (password !== confirmPassword) {
                this.errors.password.push("Passwords need to match");
                this.errors.confirmPassword.push("Passwords need to match");
                return;
            }
            
            return await runAsPublic(async (config) => {
                const passwordData = {
                    password
                };

                const referringPlatformData = {
                    password,
                    referring_platform: this.params.referringPlatform
                };
                
                let response;
                
                //If the referring platform is present, we first need to update the referring platform for the user
                if (this.params.referringPlatform) {
                    await FrontEndApiService.put(`api/user/${this.userUuid}/referring-platform`, referringPlatformData);
                    response = await FrontEndApiService.put(`${config.apiPrefix}/v1/user/${this.userUuid}`, passwordData);
                } else {
                    //If the referring platform is not present, we just follow the normal flow
                    response = await FrontEndApiService.put(`${config.apiPrefix}/v1/user/${this.userUuid}`, passwordData);
                }

                if (response.status !== 200) {
                    console.warn("Error submitting password", error);
                    this.errors.password.push('Token not found or is expired');
                    throw "Token not found or is expired";
                }

                await this.signIn();
            });
        },
        handleClose() {
            this.clearErrors();
            this.activeStepByNavFlow(STEPS.email, true, STEPS.email);
        },
        async checkAgentInvites(email = null) {
            email = email || get(this.credentials, 'email');

            return email && this.checkIfInvitedAgent(
                email, async (invitation) => {
                    // Should redirect from guest app to agent app
                    this.params.email = this.credentials?.email;
                    this.params.state = this.state?.state;
                    this.params.type = 'agent';
                    await appPageRedirect('agent', `/register?${paramsToQueryString(this.params)}`);
                }
            );
        },
        async checkSellerInvites(token = null) {
            token = token || this.params.token
            return (token || get(this.credentials, 'email')) && this.checkIfInvitedSeller(
                this.credentials?.email, token, async (invitation) => {
                    // Should redirect from guest app to seller app
                    this.params.token = token;
                    this.params.email = this.credentials?.email;
                    this.params.state = this.state?.state;

                    let inviteMeta = get(invitation || {}, 'meta', {}),
                        invitePrimarySeller = get(inviteMeta, 'primarySeller', get(inviteMeta, 'primary_seller', null)),
                        inviteSecondarySeller = get(inviteMeta, 'secondarySeller', get(inviteMeta, 'secondary_seller', null)),
                        testMail = this.params.email || invitePrimarySeller?.email;

                    if(!isEmpty(invitation?.acceptedAt)){
                        this.activeStep = STEPS.signIn;
                        this.params.email = testMail;
                        this.credentials.email = testMail;
                        return true;
                    }

                    this.params.type = 'seller';
                    if (isEmpty(invitation?.acceptedAt)) {
                        if ([invitePrimarySeller?.email, inviteSecondarySeller?.email || invitePrimarySeller?.email].includes(testMail)) {
                            this.userExists = await this.searchExistingUser(testMail);
                            if (this.userExists) {
                                this.credentials.email = testMail;
                                this.params.email = testMail;
                                this.activeStep = STEPS.signIn;
                                return;
                            }
                        }
                    }

                    await appPageRedirect('seller', `/register?${paramsToQueryString(this.params)}`);//HERE WILL BE THE SPLIT POINT FOR VWO TESTING
                                                                                                     //ONE BATCH WILL GO THE REG WAY ONE THE NEW WAY
                                                                                                     //SO I NEED TO FIGURE OUT HOW THE SPLIT WILL WORK

                }, true
            );
        },
        async checkConnectionInviation(token = null, email = null){
            if(!token && !email){
                return
            }
            return await this.validateConnectionInvitation(token, email, async (data) => {
                if(data.validToken){
                    this.params.type = this.basicInfo.accountType = data.accountTypeId === TRANSACTION_COORDINATOR ? 'transactioncoordinator' : 'agent';
                    if(!!email){
                        this.params.email = email;
                    }
                    console.log(paramsToQueryString(this.params));
                    if(data.passwordSet){
                        await appPageRedirect(this.basicInfo.accountType, `/register?${paramsToQueryString(this.params)}`);
                    }
                    
                }
            }, true);
        }
    },

    async created() {
        await this.setupWrapper(async () => {
            if (this.me?.uuid && this.me?.exists) {
                return (this.loading = true);
            }

            let params = paramsFromQueryString(this.$route.query);

            this.params = params;
            if(Boolean(params.token)){
                await this.checkConnectionInviation(params.token);
            }
            if (Boolean(params.token) && !Boolean(this.params.type)) {
                await this.checkSellerInvites(params.token);                
            }
            let hasEmail = Boolean(this.params.email);
            if (hasEmail) {
                this.credentials.email = this.params.email;
                await this.submitEmail();
            }

            if (!this.userExists) {
                if (params.state) {
                    let skipStep = !hasEmail,
                        state = await this.getLuState(params.state);

                    isEmpty(state) || (this.state.state = state.id);
                    skipStep = await this.submitState({}, skipStep);
                }

                if (params.type) {
                    this.basicInfo.accountType = params.type;
                    hasEmail && Boolean(this.state.state) && await this.chooseAccountType();
                }
            }
        });
    },
}
</script>
